import { useState, useEffect } from 'react';
import { Home, Users, Plus, FileText, MoreHorizontal, Receipt, BookOpen, CreditCard, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { useIsMobile } from '../hooks/useMediaQuery';
import { hapticClick } from '../lib/haptic';

/**
 * Mobile Bottom Navigation Bar
 * Only visible on mobile (< 768px). Fixed at bottom.
 * Provides quick access to main sections + FAB quick actions.
 */
export default function MobileBottomNav() {
  const isMobile = useIsMobile();
  const [fabOpen, setFabOpen] = useState(false);
  const [currentHash, setCurrentHash] = useState(window.location.hash || '#dashboard');

  // Listen for hash changes to update active state reactively
  useEffect(() => {
    const handler = () => setCurrentHash(window.location.hash || '#dashboard');
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  if (!isMobile) return null;

  const isActive = (href: string) => {
    if (href === '#dashboard') return currentHash === '#dashboard' || currentHash === '#' || currentHash === '';
    if (href === '#ar-customers') return currentHash.startsWith('#ar-customer') || currentHash === '#customer-mgmt';
    if (href === '#invoicing') return currentHash.startsWith('#invoicing') || currentHash === '#ar-invoicing';
    return currentHash === href;
  };

  const navItems = [
    { label: 'Home', icon: Home, href: '#dashboard' },
    { label: 'Customers', icon: Users, href: '#ar-customers' },
    { label: 'Invoices', icon: FileText, href: '#invoicing' },
    { label: 'More', icon: MoreHorizontal, href: '#settings-main' },
  ];

  const quickActions = [
    { label: 'New Invoice', icon: FileText, href: '#invoicing', color: 'bg-amber-500' },
    { label: 'New Customer', icon: Users, href: '#ar-customers', color: 'bg-cyan-500' },
    { label: 'New Expense', icon: Receipt, href: '#expense-reports', color: 'bg-violet-500' },
    { label: 'Journal Entry', icon: BookOpen, href: '#journal-entries', color: 'bg-blue-500' },
  ];

  return (
    <>
      {/* FAB Quick Actions Overlay */}
      <AnimatePresence>
        {fabOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[55] sm:hidden"
              onClick={() => setFabOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[56] flex flex-col items-center gap-3 sm:hidden"
            >
              {quickActions.map((action, i) => (
                <motion.a
                  key={action.label}
                  href={action.href}
                  onClick={() => setFabOpen(false)}
                  initial={{ opacity: 0, y: 20, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.8 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-full pl-3 pr-5 py-2.5 shadow-xl border border-slate-200 dark:border-slate-700"
                >
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-white", action.color)}>
                    <action.icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-bold text-slate-900 dark:text-white">{action.label}</span>
                </motion.a>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Nav Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 safe-bottom sm:hidden">
        <div className="flex items-center justify-around px-2 py-1">
          {/* First 2 nav items */}
          {navItems.slice(0, 2).map((item) => (
            <a key={item.label} href={item.href}
              className={cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-[56px]",
                isActive(item.href) ? "text-primary" : "text-slate-400")}>
              <item.icon className="w-5 h-5" />
              <span className="text-[9px] font-bold">{item.label}</span>
            </a>
          ))}

          {/* Center FAB */}
          <button
            onClick={() => { hapticClick(); setFabOpen(!fabOpen); }}
            className={cn(
              "flex items-center justify-center w-12 h-12 -mt-4 rounded-full shadow-lg transition-all active:scale-90",
              fabOpen
                ? "bg-slate-700 dark:bg-slate-600 shadow-slate-500/30 rotate-45"
                : "bg-primary shadow-primary/30"
            )}
          >
            {fabOpen ? <X className="w-6 h-6 text-white" /> : <Plus className="w-6 h-6 text-white" />}
          </button>

          {/* Last 2 nav items */}
          {navItems.slice(2).map((item) => (
            <a key={item.label} href={item.href}
              className={cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-[56px]",
                isActive(item.href) ? "text-primary" : "text-slate-400")}>
              <item.icon className="w-5 h-5" />
              <span className="text-[9px] font-bold">{item.label}</span>
            </a>
          ))}
        </div>
      </nav>
    </>
  );
}
